import { CurrentUser, UpdateCurrentUser } from 'smg/customer-api/currentuser';
import * as models from 'smg/customer-api/models';
import { CategoryDescriptionsFromDocument, InitEmailPreferencesPanel } from 'smg/email-preferences-page';

import { AccountReady } from '../account-session';

void main();

async function main() {
    if (await AccountReady) {
        return;
    }
    InitEmailPreferencesPanel({ mode: 'session', categoryDescriptions: CategoryDescriptionsFromDocument() });

    const $notifSms = document.querySelector('#notif-sms') as HTMLInputElement;
    const $smsLoading = document.querySelector('#sms-loading') as HTMLElement;
    const $notifSmsLoadFailed = document.querySelector('#notif-sms-load-failed') as HTMLElement;
    const $notifSmsRetry = document.querySelector('#notif-sms-retry') as HTMLButtonElement;
    const $offNote = document.querySelector('#sms-off-note') as HTMLElement;
    const $categories = document.querySelector('#text-categories') as HTMLElement;
    const $template = document.querySelector('#text-category-template') as HTMLTemplateElement;

    // The saved chip and failed line a save reports through, per control.
    interface saveFeedback {
        $saved: HTMLElement;
        $failed: HTMLElement;
        savedTimer: number;
    }
    const masterFeedback: saveFeedback = {
        $saved: document.querySelector('#notif-sms-saved') as HTMLElement,
        $failed: document.querySelector('#notif-sms-failed') as HTMLElement,
        savedTimer: 0,
    };
    function clearFeedback(feedback: saveFeedback) {
        window.clearTimeout(feedback.savedTimer);
        feedback.savedTimer = 0;
        feedback.$saved.classList.add('d-none');
        feedback.$failed.classList.add('d-none');
    }
    function showSaved(feedback: saveFeedback) {
        feedback.$saved.classList.remove('d-none');
        feedback.savedTimer = window.setTimeout(() => {
            feedback.$saved.classList.add('d-none');
            feedback.savedTimer = 0;
        }, 2500);
    }

    interface categoryRow {
        $row: HTMLElement;
        $switch: HTMLInputElement;
        feedback: saveFeedback;
    }
    const rows = new Map<string, categoryRow>();

    let authoritative: models.NotificationChannelPreferences | null = null;

    type smsCommand =
        { kind: 'master'; enabled: boolean } |
        { kind: 'category'; name: string; enabled: boolean };
    // Commands the customer has made that the server hasn't confirmed yet, in
    // order. Everything drawn is the authoritative state folded through these,
    // so a failed command simply disappears from the fold and the panel falls
    // back to the truth, not to a guess.
    const pending: smsCommand[] = [];

    // The one transition, shared by the optimistic fold and the post-save
    // render — they can't disagree. Each save sends only itself.
    function applyCommand(state: models.NotificationChannelPreferences, command: smsCommand): models.NotificationChannelPreferences {
        const next = new models.NotificationChannelPreferences(state);
        next.categories = state.categories.map(category => new models.NotificationCategoryPreference(category));
        if (command.kind === 'master') {
            next.enabled = command.enabled;
            return next;
        }
        for (const category of next.categories) {
            if (category.name === command.name) {
                category.enabled = command.enabled;
            }
        }
        return next;
    }
    function fold(commands: smsCommand[]): models.NotificationChannelPreferences {
        let state = new models.NotificationChannelPreferences(authoritative ?? undefined);
        if (authoritative) {
            state.categories = authoritative.categories.map(category => new models.NotificationCategoryPreference(category));
        }
        for (const command of commands) {
            state = applyCommand(state, command);
        }
        return state;
    }

    let queue: Promise<unknown> = Promise.resolve();
    function runQueued<T>(task: () => Promise<T>): Promise<T> {
        const next = queue.then(task, task);
        queue = next.catch(() => undefined);
        return next;
    }

    function setBusy($control: HTMLInputElement, busy: boolean) {
        if (busy) {
            $control.setAttribute('aria-busy', 'true');
        } else {
            $control.removeAttribute('aria-busy');
        }
    }

    function render() {
        if (!authoritative) {
            return;
        }
        const folded = fold(pending);
        $notifSms.checked = folded.enabled;
        $offNote.classList.toggle('d-none', folded.enabled);
        for (const [name, row] of rows) {
            const category = folded.categories.find(item => item.name === name);
            row.$switch.checked = category?.enabled === true;
            row.$row.classList.toggle('eprefs-muted', !folded.enabled);
        }
    }

    function bindRows(categories: models.NotificationCategoryPreference[]) {
        for (const category of categories) {
            const $frag = $template.content.cloneNode(true) as DocumentFragment;
            const $row = $frag.firstElementChild as HTMLElement;
            const $name = $row.querySelector('.sms-category-name') as HTMLLabelElement;
            const $switch = $row.querySelector('.sms-category-switch') as HTMLInputElement;
            $name.textContent = category.name;
            $switch.setAttribute('aria-label', category.name);
            $switch.id = `sms-category-${category.name.replace(/[^A-Za-z0-9]+/g, '-')}`;
            $name.htmlFor = $switch.id;
            const row: categoryRow = {
                $row,
                $switch,
                feedback: {
                    $saved: $row.querySelector('.sms-category-saved') as HTMLElement,
                    $failed: $row.querySelector('.sms-category-failed') as HTMLElement,
                    savedTimer: 0,
                },
            };
            rows.set(category.name, row);
            $switch.addEventListener('change', () => {
                void submit({ kind: 'category', name: category.name, enabled: $switch.checked }, $switch, row.feedback);
            });
            $categories.appendChild($row);
        }
    }

    // One path for every command. The control is disabled until its save
    // settles (so at most one command per control is ever pending), busy while
    // its request is in flight; the switch position itself comes from
    // render(), never from the click.
    async function submit(command: smsCommand, $control: HTMLInputElement, feedback: saveFeedback): Promise<void> {
        $control.disabled = true;
        clearFeedback(feedback);
        pending.push(command);
        render();
        try {
            await runQueued(async () => {
                setBusy($control, true);
                try {
                    let resp: models.CurrentUserResponse;
                    if (command.kind === 'master') {
                        let state: models.TextMessagesStateValue;
                        if (command.enabled) {
                            state = models.TextMessagesStateValue.TEXT_MESSAGES_STATE_ON;
                        } else {
                            state = models.TextMessagesStateValue.TEXT_MESSAGES_STATE_OFF;
                        }
                        resp = await UpdateCurrentUser({
                            notificationPreferences: { textMessages: { state } },
                        });
                    } else {
                        resp = await UpdateCurrentUser({
                            notificationPreferences: {
                                textMessages: { categories: { [command.name]: command.enabled } },
                            },
                        });
                    }
                    authoritative = resp.notificationPreferences.textMessages;
                } finally {
                    setBusy($control, false);
                    pending.splice(pending.indexOf(command), 1);
                    render();
                }
            });
            showSaved(feedback);
        } catch {
            feedback.$failed.classList.remove('d-none');
        } finally {
            $control.disabled = false;
        }
    }

    $notifSms.addEventListener('change', () => {
        void submit({ kind: 'master', enabled: $notifSms.checked }, $notifSms, masterFeedback);
    });

    let smsLoading = false;
    async function loadSms() {
        if (smsLoading) {
            return;
        }
        smsLoading = true;
        $notifSmsRetry.disabled = true;
        $smsLoading.classList.remove('d-none');
        $notifSmsLoadFailed.classList.add('d-none');
        try {
            const resp = await CurrentUser();
            authoritative = resp.notificationPreferences.textMessages;
            if (rows.size === 0) {
                bindRows(authoritative.categories);
            }
            $notifSms.disabled = false;
            for (const row of rows.values()) {
                row.$switch.disabled = false;
            }
            render();
        } catch {
            $notifSmsLoadFailed.classList.remove('d-none');
        } finally {
            $smsLoading.classList.add('d-none');
            smsLoading = false;
            $notifSmsRetry.disabled = false;
        }
    }

    $notifSmsRetry.addEventListener('click', () => {
        void loadSms();
    });
    void loadSms();
}
